import { useCallback, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AnyObjectSchema } from 'yup';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { popModal } from '@/redux/modals/actions';
import { submitWithTrimming, isPromise } from '@/util/util';
import { errorMessage } from '@/util/validation/helpers';

type Props = {
  onEdit: (data: Record<string, string | number>) => void;
  fieldName: string;
  fieldValue?: string | number;
  rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  multiline?: boolean;
  validationSchema?: AnyObjectSchema;
  resource?: string;
};

export default function ModalEdit({
  onEdit,
  fieldName,
  fieldValue = '',
  rules = {},
  multiline = false,
  validationSchema,
  resource,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: { [fieldName]: fieldValue },
    resolver: validationSchema && yupResolver(validationSchema),
  });
  const {
    control,
    formState: { errors },
  } = formMethods;

  const fieldErrorMessage = useMemo(
    () => errorMessage({ field: errors[fieldName], ...rules }),
    [errors, fieldName, rules]
  );

  const onSubmit = useCallback(
    async (data: Record<string, string | number>) => {
      try {
        setIsLoading(true);
        if (isPromise(onEdit)) {
          await onEdit(data);
        } else {
          onEdit(data);
        }
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [onEdit]
  );

  const handleClose = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modals.edit', { 0: resource })}</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <form
          className="space-y-6"
          onSubmit={submitWithTrimming(formMethods, onSubmit)}
          autoComplete="off"
        >
          <div className="space-y-2">
            <Label htmlFor={fieldName} className="text-sm font-medium">
              {t(`common.${fieldName}`, { defaultValue: fieldName.replace('_', ' ') })}
            </Label>
            <Controller
              control={control}
              name={fieldName}
              rules={rules}
              render={({ field }) =>
                multiline ? (
                  <Textarea
                    id={fieldName}
                    placeholder={fieldValue?.toString() ?? ''}
                    {...field}
                    value={field.value ?? ''}
                    autoFocus
                  />
                ) : (
                  <Input
                    id={fieldName}
                    placeholder={fieldValue?.toString() ?? ''}
                    {...field}
                    value={field.value ?? ''}
                    autoFocus
                  />
                )
              }
            />
            {fieldErrorMessage && <p className="text-sm text-destructive">{fieldErrorMessage}</p>}
          </div>

          <DialogFooter className="sm:flex-row-reverse sm:justify-start">
            <Button type="submit" isLoading={isLoading}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
